/**
 * InclusionList — left panel. Merges persisted inclusions with pending ops
 * to produce an "effective" list, then renders each as an InclusionCard.
 *
 * No network calls; no resolver work. The server tells us which entries
 * belong to which inclusion via MaterializedEntry.sourceInclusionId, and
 * we group by that to hand each card its own slice.
 */

import { useMemo } from 'react';
import type {
  GetCandidatesResponse,
  MaterializedEntry,
  PendingOp,
  PoolFlagDefaults,
  PoolInclusion,
  PoolRefinement,
  UpdateInclusionInput,
  UpdateRefinementInput,
} from '@interlucent/organization-protocol';
import { InclusionCard, type EffectiveInclusion } from './InclusionCard';
import styles from './InclusionList.module.css';
import {
  type CandidateIndex,
  type DisplayRefinement,
  makeStagedInclusion,
  makeStagedRefinement,
} from './types';

export interface InclusionListProps {
  poolId: string;
  inclusions: PoolInclusion[];
  /** Pre-filtered by parent: refinements whose id is in the server's
   *  staleRefinementIds have already been stripped out upstream. */
  refinements: PoolRefinement[];
  entries: MaterializedEntry[];
  staleInclusionIds: Set<string>;
  pendingOps: PendingOp[];
  poolDefaults: PoolFlagDefaults;
  candidateIndex: CandidateIndex;
  candidates: GetCandidatesResponse;
  onOp: (op: PendingOp) => void;
  onDropStagedAdd: (stagedIndex: number) => void;
  onUndoRemoveInclusion: (id: string) => void;
  onUndoUpdateInclusion: (id: string) => void;
}

export function InclusionList({
  poolId,
  inclusions,
  refinements,
  entries,
  staleInclusionIds,
  pendingOps,
  poolDefaults,
  candidateIndex,
  candidates,
  onOp,
  onDropStagedAdd,
  onUndoRemoveInclusion,
  onUndoUpdateInclusion,
}: InclusionListProps) {
  // Build effective inclusions: persisted rows with pending updates merged,
  // plus any addInclusion ops not yet saved. Order is: persisted first (by
  // server order), then staged (in op order).
  const effectiveInclusions = useMemo<EffectiveInclusion[]>(() => {
    const out: EffectiveInclusion[] = [];

    // Index pending updates and removals by id (local or server).
    const updatesById = new Map<string, UpdateInclusionInput>();
    const removalsById = new Set<string>();
    for (const op of pendingOps) {
      if (op.kind === 'updateInclusion') {
        const prev = updatesById.get(op.id) ?? {};
        updatesById.set(op.id, { ...prev, ...op.payload });
      } else if (op.kind === 'removeInclusion') {
        removalsById.add(op.id);
      }
    }

    for (const inc of inclusions) {
      const upd = updatesById.get(inc.id);
      const effective: EffectiveInclusion = {
        kind: 'persisted',
        id: inc.id,
        inclusion: inc,
        onlyDefaultVersion: pickBool(upd, 'onlyDefaultVersion', inc.onlyDefaultVersion),
        precacheOverride: pickNullable(upd, 'precacheOverride', inc.precacheOverride),
        prelaunchOverride: pickNullable(upd, 'prelaunchOverride', inc.prelaunchOverride),
        warmTargetOverride: pickNullableNumber(upd, 'warmTargetOverride', inc.warmTargetOverride),
        multiplexOverride: pickNullable(upd, 'multiplexOverride', inc.multiplexOverride),
        pendingUpdate: !!upd,
        pendingRemoval: removalsById.has(inc.id),
      };
      out.push(effective);
    }

    // Staged addInclusion ops (id-less until save). The staged-<index> id
    // lets update/remove ops for it be keyed off the same string the server
    // returns once persisted; the editor reducer maps them appropriately.
    for (let i = 0; i < pendingOps.length; i++) {
      const op = pendingOps[i];
      if (op.kind !== 'addInclusion') continue;
      const staged = makeStagedInclusion(poolId, op.payload, i);
      // Apply any updateInclusion ops targeting this staged row.
      const upd = updatesById.get(staged.id);
      out.push({
        kind: 'staged',
        id: staged.id,
        staged,
        onlyDefaultVersion: pickBool(upd, 'onlyDefaultVersion', staged.onlyDefaultVersion),
        precacheOverride: pickNullable(upd, 'precacheOverride', staged.precacheOverride),
        prelaunchOverride: pickNullable(upd, 'prelaunchOverride', staged.prelaunchOverride),
        warmTargetOverride: pickNullableNumber(upd, 'warmTargetOverride', staged.warmTargetOverride),
        multiplexOverride: pickNullable(upd, 'multiplexOverride', staged.multiplexOverride),
      });
    }

    return out;
  }, [inclusions, pendingOps, poolId]);

  // Build effective refinements for card consumption. Same merge pattern.
  const effectiveRefinements = useMemo<DisplayRefinement[]>(() => {
    const out: DisplayRefinement[] = [];
    const updatesById = new Map<string, UpdateRefinementInput>();
    const removalsById = new Set<string>();
    for (const op of pendingOps) {
      if (op.kind === 'updateRefinement') {
        const prev = updatesById.get(op.id) ?? {};
        updatesById.set(op.id, { ...prev, ...op.payload });
      } else if (op.kind === 'removeRefinement') {
        removalsById.add(op.id);
      }
    }

    for (const r of refinements) {
      if (removalsById.has(r.id)) continue;
      const upd = updatesById.get(r.id);
      if (!upd) {
        out.push({ kind: 'persisted', refinement: r });
        continue;
      }
      // Apply updates.
      const merged: PoolRefinement = {
        ...r,
        precacheOverride: pickNullable(upd, 'precacheOverride', r.precacheOverride),
        prelaunchOverride: pickNullable(upd, 'prelaunchOverride', r.prelaunchOverride),
        warmTargetOverride: pickNullableNumber(upd, 'warmTargetOverride', r.warmTargetOverride),
        multiplexOverride: pickNullable(upd, 'multiplexOverride', r.multiplexOverride),
      };
      out.push({ kind: 'persisted', refinement: merged, pendingUpdate: {} });
    }

    for (let i = 0; i < pendingOps.length; i++) {
      const op = pendingOps[i];
      if (op.kind !== 'addRefinement') continue;
      const staged = makeStagedRefinement(poolId, op.payload, i);
      // Apply subsequent update ops against the staged-<i> id.
      const upd = updatesById.get(staged.id);
      const applied = upd
        ? {
            ...staged,
            precacheOverride: pickNullable(upd, 'precacheOverride', staged.precacheOverride),
            prelaunchOverride: pickNullable(upd, 'prelaunchOverride', staged.prelaunchOverride),
            warmTargetOverride: pickNullableNumber(upd, 'warmTargetOverride', staged.warmTargetOverride),
            multiplexOverride: pickNullable(upd, 'multiplexOverride', staged.multiplexOverride),
          }
        : staged;
      out.push({ kind: 'staged', staged: applied });
    }
    return out;
  }, [refinements, pendingOps, poolId]);

  // Group materialized entries by sourceInclusionId.
  const entriesByInclusion = useMemo(() => {
    const map = new Map<string, MaterializedEntry[]>();
    for (const e of entries) {
      const list = map.get(e.sourceInclusionId) ?? [];
      list.push(e);
      map.set(e.sourceInclusionId, list);
    }
    return map;
  }, [entries]);

  const persistedCount = effectiveInclusions.filter((i) => i.kind === 'persisted').length;
  const stagedCount = effectiveInclusions.filter((i) => i.kind === 'staged').length;

  return (
    <div className={styles.panel}>
      <div className={styles.panelHeader}>
        <div className={styles.panelTitle}>
          Included{' '}
          {effectiveInclusions.length > 0 && (
            <span style={{ fontWeight: 'var(--weight-medium)', opacity: 0.85 }}>
              ({effectiveInclusions.length})
            </span>
          )}
        </div>
        <div className={styles.panelCounts}>
          {persistedCount} saved{stagedCount > 0 ? ` · ${stagedCount} staged` : ''}
        </div>
      </div>
      <div className={styles.list}>
        {effectiveInclusions.length === 0 && (
          <div className={styles.emptyState}>
            No inclusions yet. Pick a node on the right to stage an addition.
          </div>
        )}
        {effectiveInclusions.map((eff) => {
          const cardEntries =
            eff.kind === 'persisted' ? entriesByInclusion.get(eff.inclusion.id) ?? [] : [];
          return (
            <InclusionCard
              key={eff.id}
              effective={eff}
              refinements={effectiveRefinements}
              entries={cardEntries}
              poolDefaults={poolDefaults}
              stale={eff.kind === 'persisted' && staleInclusionIds.has(eff.inclusion.id)}
              candidateIndex={candidateIndex}
              candidates={candidates}
              onOp={onOp}
              onUndoStagedAdd={eff.kind === 'staged' ? () => onDropStagedAdd(eff.staged.stagedIndex) : undefined}
              onUndoStagedRemoval={
                eff.kind === 'persisted' && eff.pendingRemoval
                  ? () => onUndoRemoveInclusion(eff.inclusion.id)
                  : undefined
              }
              onUndoStagedUpdate={
                eff.kind === 'persisted' && eff.pendingUpdate
                  ? () => onUndoUpdateInclusion(eff.inclusion.id)
                  : undefined
              }
            />
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Merge helpers
//
// `upd?.onlyDefaultVersion ?? fallback` swallows an explicit `false` in the
// pending update (nullish-coalescing treats `undefined` AND `null` as the
// trigger). Same hazard exists for the nullable flag overrides, where an
// operator's explicit "set back to inherit" (null) is semantically distinct
// from "field not in the patch" (undefined). Use `in` narrowing so the
// staged intent is preserved verbatim.
// ---------------------------------------------------------------------------

function pickBool<K extends string>(
  patch: Partial<Record<K, boolean>> | undefined,
  key: K,
  fallback: boolean,
): boolean {
  if (patch && key in patch) {
    const v = patch[key];
    if (typeof v === 'boolean') return v;
  }
  return fallback;
}

function pickNullable<K extends string>(
  patch: Partial<Record<K, boolean | null>> | undefined,
  key: K,
  fallback: boolean | null,
): boolean | null {
  if (patch && key in patch) {
    const v = patch[key];
    if (v === null || typeof v === 'boolean') return v;
  }
  return fallback;
}

function pickNullableNumber<K extends string>(
  patch: Partial<Record<K, number | null>> | undefined,
  key: K,
  fallback: number | null,
): number | null {
  if (patch && key in patch) {
    const v = patch[key];
    if (v === null || typeof v === 'number') return v;
  }
  return fallback;
}
